/**
 * Wallet Transactions Context
 *
 * Global context for managing transaction records (payments and deposits) with real-time Firestore sync.
 * Provides instant writes (no debouncing) and real-time updates via onSnapshot.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { DocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useMaybeOLVRate } from './OLVRateContext';
import type { Transaction, TransactionType, TransactionStatus, TransactionMethod } from '../types/transactions';
import {
  createTransaction,
  onTransactionsSnapshot,
  listTransactions,
  CreateTransactionInput,
} from '../repositories/walletTransactionsRepository';

// ============================================================================
// Types
// ============================================================================

interface WalletTransactionsState {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  version: number;
  latestTransaction: Transaction | null;
}

interface WalletTransactionsContextValue {
  state: WalletTransactionsState;
  /** Add a new transaction record (instant write to Firestore) */
  addTransaction: (input: AddTransactionInput) => Promise<Transaction | null>;
  /** Load more transactions (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh transactions from Firestore */
  refresh: () => void;
  /** Get a transaction by ID */
  getTransactionById: (id: string) => Transaction | undefined;
}

export interface AddTransactionInput {
  /** Transaction type: payment (out) or deposit (in) */
  type: TransactionType;
  /** Summary description */
  summary: string;
  /** Amount in USD (always positive) */
  amountUsd: number;
  /** Transaction method */
  method: TransactionMethod;
  /** Tags for categorization */
  tags?: string[];
  /** External reference ID */
  externalRef?: string;
  /** Initial status (defaults to 'pending') */
  status?: TransactionStatus;
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;

// ============================================================================
// Reducer
// ============================================================================

type WalletTransactionsAction =
  | { type: 'SET_TRANSACTIONS'; payload: { items: Transaction[]; hasMore: boolean } }
  | { type: 'APPEND_TRANSACTIONS'; payload: { items: Transaction[]; hasMore: boolean } }
  | { type: 'ADD_TRANSACTION'; payload: Transaction }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR' };

const initialState: WalletTransactionsState = {
  transactions: [],
  isLoading: true,
  error: null,
  totalCount: 0,
  hasMore: false,
  version: 0,
  latestTransaction: null,
};

function walletTransactionsReducer(state: WalletTransactionsState, action: WalletTransactionsAction): WalletTransactionsState {
  switch (action.type) {
    case 'SET_TRANSACTIONS':
      return {
        ...state,
        transactions: action.payload.items,
        totalCount: action.payload.items.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null,
        version: state.version + 1,
      };
    case 'APPEND_TRANSACTIONS':
      const combined = [...state.transactions, ...action.payload.items];
      return {
        ...state,
        transactions: combined,
        totalCount: combined.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        version: state.version + 1,
      };
    case 'ADD_TRANSACTION':
      const newTransactions = [action.payload, ...state.transactions];
      return {
        ...state,
        transactions: newTransactions,
        totalCount: state.totalCount + 1,
        version: state.version + 1,
        latestTransaction: action.payload,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

const WalletTransactionsContext = createContext<WalletTransactionsContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function WalletTransactionsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRate = useMaybeOLVRate();
  const [state, dispatch] = useReducer(walletTransactionsReducer, initialState);
  const cursorRef = useRef<DocumentSnapshot | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  // Set up real-time listener when user changes
  useEffect(() => {
    if (!user?.id) {
      dispatch({ type: 'CLEAR' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Start real-time sync
    unsubRef.current = onTransactionsSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_TRANSACTIONS',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [user?.id]);

  // Add a new transaction (instant write)
  const addTransaction = useCallback(
    async (input: AddTransactionInput): Promise<Transaction | null> => {
      if (!user?.id) return null;

      const rate = olvRate?.getEffectiveRate() || 0.001;
      const memberId = user.profileId || user.id;

      const createInput: CreateTransactionInput = {
        type: input.type,
        summary: input.summary,
        memberId,
        amountUsd: input.amountUsd,
        amountOlv: input.amountUsd / rate,
        olvRateAtTime: rate,
        method: input.method,
        tags: input.tags,
        externalRef: input.externalRef,
        status: input.status || 'pending',
      };

      try {
        const transaction = await createTransaction(user.id, createInput);
        // The real-time listener will update the state automatically
        // But we also add it optimistically for instant UI feedback
        dispatch({ type: 'ADD_TRANSACTION', payload: transaction });
        return transaction;
      } catch (error) {
        console.error('[WalletTransactionsContext] Failed to create transaction:', error);
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
        return null;
      }
    },
    [user?.id, user?.profileId, olvRate]
  );

  // Load more transactions (pagination)
  const loadMore = useCallback(async () => {
    if (!user?.id || !cursorRef.current || state.isLoading) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const result = await listTransactions(user.id, {
        limit: PAGE_SIZE,
        startAfter: cursorRef.current,
      });

      cursorRef.current = result.nextCursor;
      dispatch({
        type: 'APPEND_TRANSACTIONS',
        payload: { items: result.items, hasMore: result.items.length >= PAGE_SIZE },
      });
    } catch (error) {
      console.error('[WalletTransactionsContext] Failed to load more transactions:', error);
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    }
  }, [user?.id, state.isLoading]);

  // Refresh transactions
  const refresh = useCallback(() => {
    if (!user?.id) return;

    // Clean up and re-subscribe to trigger a fresh load
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    unsubRef.current = onTransactionsSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_TRANSACTIONS',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );
  }, [user?.id]);

  // Get transaction by ID
  const getTransactionById = useCallback(
    (id: string): Transaction | undefined => {
      return state.transactions.find((t) => t.id === id);
    },
    [state.transactions]
  );

  const value: WalletTransactionsContextValue = {
    state,
    addTransaction,
    loadMore,
    refresh,
    getTransactionById,
  };

  return (
    <WalletTransactionsContext.Provider value={value}>
      {children}
    </WalletTransactionsContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useWalletTransactions(): WalletTransactionsContextValue {
  const context = useContext(WalletTransactionsContext);
  if (!context) {
    throw new Error('useWalletTransactions must be used within a WalletTransactionsProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useMaybeWalletTransactions(): WalletTransactionsContextValue | null {
  return useContext(WalletTransactionsContext);
}
